/**
 * Pluggable View Registry
 *
 * Manages declarations of pluggable/replaceable views throughout the application.
 * Uses explicit declarations to handle conditional rendering scenarios.
 */
import { hasValue } from "@utils";
/**
 * Global registry storing all pluggable view declarations
 */
const declarationsRegistry = new Map();
/**
 * Protected view IDs that should not be unregistered
 * Used by PageEditor to prevent unregistration of selected view + ancestors
 */
const protectedViewIds = new Set();
const listeners = [];
/**
 * Notify all listeners of registry changes
 */
function notifyChange() {
    listeners.forEach(listener => listener());
}
/**
 * Subscribe to registry changes
 * @returns Unsubscribe function
 */
export function onRegistryChange(listener) {
    listeners.push(listener);
    return () => {
        const index = listeners.indexOf(listener);
        if (index > -1) {
            listeners.splice(index, 1);
        }
    };
}
/**
 * Register a pluggable view declaration
 *
 * @example
 * ```typescript
 * registerPluggableViewDeclaration({
 *   viewId: '/location/details/panel',
 *   default: 'com.iviva.locations/widget/InfoPanel'
 * });
 * ```
 */
export function registerPluggableViewDeclaration(declaration) {
    var _a;
    const normalizedViewId = declaration.viewId.toLowerCase();
    declarationsRegistry.set(normalizedViewId, Object.assign(Object.assign({}, declaration), { viewId: normalizedViewId, parentViewId: hasValue(declaration === null || declaration === void 0 ? void 0 : declaration.parentViewId) ? (_a = declaration === null || declaration === void 0 ? void 0 : declaration.parentViewId) === null || _a === void 0 ? void 0 : _a.toLowerCase() : null }));
    notifyChange();
}
/**
 * Get all pluggable view declarations for a specific page URL
 *
 * @example
 * ```typescript
 * const views = getPluggableViewsForUrl('/view/location/details');
 * // Returns all declared views for that page
 * ```
 */
export function getAllPluggableViews() {
    return Array.from(declarationsRegistry.values());
}
/**
 * Get a specific pluggable view declaration by viewId
 */
export function getPluggableViewDeclaration(viewId) {
    return declarationsRegistry.get(viewId.toLowerCase());
}
/**
 * Clear all pluggable view declarations
 * Useful for testing
 */
export function clearAllPluggableViews() {
    declarationsRegistry.clear();
}
export function unregisterPluggableView(viewId) {
    const normalizedViewId = viewId.toLowerCase();
    // Don't unregister if view is protected (e.g., selected in PageEditor)
    if (protectedViewIds.has(normalizedViewId)) {
        return false;
    }
    const result = declarationsRegistry.delete(normalizedViewId);
    if (result) {
        notifyChange();
    }
    return result;
}
/**
 * Get all ancestor viewIds for a given viewId by traversing parentViewId chain
 *
 * @example
 * getAncestors('/Location/details/other')
 * // Returns: ['/Location/details'] (if that's the parent)
 */
function getAncestors(viewId) {
    const ancestors = [];
    let current = declarationsRegistry.get(viewId.toLowerCase());
    while (current === null || current === void 0 ? void 0 : current.parentViewId) {
        const normalizedParentId = current.parentViewId.toLowerCase();
        ancestors.push(normalizedParentId);
        current = declarationsRegistry.get(normalizedParentId);
    }
    return ancestors;
}
/**
 * Protect a view and all its ancestors from auto-unregister
 * Used by PageEditor to keep selected view hierarchy active
 *
 * @param viewId - The view to protect (along with ancestors)
 *
 * @example
 * protectViewWithAncestors('/Location/details/other');
 * // Protects both '/Location/details/other' and '/Location/details'
 */
export function protectViewWithAncestors(viewId) {
    if (!hasValue(viewId))
        return;
    const normalizedViewId = viewId.toLowerCase();
    protectedViewIds.add(normalizedViewId);
    // Also protect all ancestors
    const ancestors = getAncestors(normalizedViewId);
    ancestors.forEach(ancestorId => protectedViewIds.add(ancestorId));
}
/**
 * Clear all view protections
 * Should be called when PageEditor unmounts
 */
export function clearProtectedViews() {
    protectedViewIds.clear();
}
